import {
  type FC, useCallback, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { CONFIG } from '~/_shared/constants/config';
import { TwoFactorMode } from '~/_shared/constants/twoFactorMode.enum';
import { useOnMount } from '~/_shared/hooks/useOnMount';
import { GAEventName } from '~/_shared/utils/3rdPartyIntegrations/ga/ga.types';
import { sendSignupFunnelEvent } from '~/_shared/utils/3rdPartyIntegrations/ga/signupFunnel.helpers';
import { getTeamMemberError } from '~/clientTeamManagement/clientTeamManagement.helpers';
import { isSalesforceAuthRoute } from '~/salesforce/salesforce.helpers';
import { sendTwoFactorEmailRequest } from '~/store/userData/repository/userData.repository';
import { useUserDataSelector } from '~/store/userData/userData.selectors';
import { useAuthContext } from '../../authContext';
import { useSsoLogin } from '../sso/useSsoLogin';
import { Login2FaFormComponent } from './login2FaForm.component';
import { LoginFormComponent } from './loginForm.component';
import { useUserSign } from './useUserSign';

type LoginFormContainerProps = {
  onLoginFormSubmit?: () => void;
};

export const LoginFormContainer: FC<LoginFormContainerProps> = (props) => {
  const { isLoading, error, mfaMode, availableMfaModes, authenticateUserRequest, authenticate2FaUserRequest,
    resetTwoFactorData, changeMfaMode } = useUserSign();
  const { error: refreshTokenError, clearError } = useAuthContext();
  const { isLoading: isSsoLoading, onGoogleLogin, onMicrosoftLogin } = useSsoLogin('login');
  const teamMemberError = getTeamMemberError();
  const [usedEmail, setUsedEmail] = useState('');
  const [usedPassword, setUsedPassword] = useState('');
  const isLoadingFromState = useUserDataSelector().isLoading;
  const locationState = useLocation().state;

  const fromPathname = locationState?.from?.pathname || '/';
  const isSalesforceAuthProcess = isSalesforceAuthRoute(fromPathname);
  const onlyEmailLogin = isSalesforceAuthProcess;
  const disallowRegister = isSalesforceAuthProcess;

  useOnMount(() => {
    sendSignupFunnelEvent(GAEventName.SignupPageView, { page: 'login' });
  });

  const dispatchProps = useMemo(() => {
    return ({
      onFormSubmit: async (email: string, password: string, rememberMe: boolean) => {
        clearError();
        setUsedEmail(email);
        setUsedPassword(password);
        await authenticateUserRequest({ email, password, rememberMe });

        const onLoginFormSubmit = props.onLoginFormSubmit;
        onLoginFormSubmit?.();
      },
      on2FASubmit: async (code: string) => {
        clearError();
        await authenticate2FaUserRequest(code);
      },
      reset2FaMode: () => {
        resetTwoFactorData();
      },
    });
  }, [authenticate2FaUserRequest, authenticateUserRequest, clearError, props.onLoginFormSubmit, resetTwoFactorData]);

  const resendMFAEmail = useCallback(() =>
    sendTwoFactorEmailRequest(usedEmail, usedPassword),
  [usedEmail, usedPassword]);

  const onLegacyLogin = useCallback(() => {
    location.href = CONFIG.LEGACY_LOGIN_URL;
  }, []);

  const isAnythingLoading = isLoading || isLoadingFromState || isSsoLoading;

  return (
    <div>
      {!mfaMode ? (
        <LoginFormComponent
          error={error || refreshTokenError || teamMemberError || ''}
          isLoading={isAnythingLoading}
          onSubmit={dispatchProps.onFormSubmit}
          onGoogleLogin={onGoogleLogin}
          onMicrosoftLogin={onMicrosoftLogin}
          onLegacyLogin={onLegacyLogin}
          disallowRegister={disallowRegister}
          onlyEmailLogin={onlyEmailLogin}
        />
      ) : (
        <Login2FaFormComponent
          error={error || refreshTokenError || ''}
          isLoading={isAnythingLoading}
          mfaMode={mfaMode}
          availableMfaModes={availableMfaModes}
          onSubmit={dispatchProps.on2FASubmit}
          repeatMFA={mfaMode === TwoFactorMode.google ? undefined : resendMFAEmail}
          reset2FaMode={dispatchProps.reset2FaMode}
          onChangeMfaMode={changeMfaMode}
        />
      )}
    </div>
  );
};
